import type { JSX } from 'react';
import { Link, useParams } from 'react-router-dom';
import { FEATURES } from '@yuan-math/config';
import { allDependents, missingPrerequisites, pathTo } from '@yuan-math/curriculum';
import { diagnoseStruggle, MASTERY_THRESHOLD } from '@yuan-math/learning';
import { misconception } from '@yuan-math/exercises';
import { Card, MasteryBar, SectionHeading, StatusPill } from '@yuan-math/ui';
import { registry, useLearner } from '../state/LearnerContext.tsx';
import { NotFoundPage } from './NotFoundPage.tsx';

/**
 * One concept, in full.
 *
 * This is where the graph pays off for a student: the four components of their
 * mastery, what the concept rests on, what it unlocks, and — if they are
 * struggling — a specific diagnosis rather than "practise more".
 */
export function ConceptPage(): JSX.Element {
  const { conceptId } = useParams();
  const { profile, masteryOf, masteredConceptIds, knownConceptIds } = useLearner();

  const concept = conceptId ? registry.concept(conceptId) : undefined;
  if (!concept || !conceptId) return <NotFoundPage />;

  const mastery = masteryOf(conceptId);
  const lessons = registry.lessonsForConcept(conceptId);
  const missing = missingPrerequisites(registry.graph, conceptId, knownConceptIds);
  const unlocks = allDependents(registry.graph, conceptId);
  const state = profile?.concepts[conceptId];
  const struggling = (state?.attempts ?? 0) >= 3 && mastery.overall < MASTERY_THRESHOLD;

  const diagnosis =
    profile && struggling ? diagnoseStruggle({ registry, profile, now: Date.now() }, conceptId) : undefined;

  const route = profile ? pathTo(registry.graph, conceptId, knownConceptIds) : [];

  const status = concept.status === 'planned'
    ? 'planned'
    : masteredConceptIds.has(conceptId)
      ? 'mastered'
      : (state?.attempts ?? 0) > 0
        ? 'in-progress'
        : missing.length === 0
          ? 'ready'
          : 'locked';

  return (
    <div className="page page--wide">
      <nav className="breadcrumb" aria-label="Breadcrumb">
        <Link to="/map">Math Map</Link>
        <span aria-hidden="true">/</span>
        <span>{registry.strand(concept.strand)?.title}</span>
      </nav>

      <header className="page-header">
        <div className="concept-title-row">
          <h1 className="page__title">{concept.title}</h1>
          <StatusPill status={status} />
        </div>
        <p className="page__lead">{concept.summary}</p>
        <p className="concept-levels">
          Usually taught at {concept.levels.map((id) => registry.level(id)?.title ?? id).join(', ') || 'various levels'} —
          but not restricted to it. If you need this concept, it is available to you.
        </p>
      </header>

      {concept.status === 'available' ? (
        <section className="dashboard-section">
          <SectionHeading
            title="Your mastery"
            description="Four separate measurements, because being accurate is not the same as remembering, and remembering is not the same as being able to apply it."
          />
          <div className="mastery-grid">
            <Card className="mastery-overall">
              <p className="mastery-overall__value">{Math.round(mastery.overall * 100)}%</p>
              <p className="mastery-overall__label">Overall</p>
              {mastery.provisional && mastery.overall > 0 ? (
                <p className="mastery-overall__note">Provisional — not enough evidence yet to be sure.</p>
              ) : null}
            </Card>
            <Card className="mastery-breakdown">
              <MasteryBar value={mastery.understanding} label="Understanding" />
              <MasteryBar value={mastery.accuracy} label="Accuracy" />
              <MasteryBar value={mastery.retention} label="Retention" />
              <MasteryBar value={mastery.application} label="Application" />
            </Card>
          </div>
        </section>
      ) : (
        <Card>
          <p>
            This concept is part of the map but has no lessons yet. It is shown so you can see the shape of the
            subject and where your current work is heading.
          </p>
        </Card>
      )}

      {diagnosis && (diagnosis.weakPrerequisites.length > 0 || diagnosis.repeatedMisconceptions.length > 0) ? (
        <section className="dashboard-section">
          <SectionHeading title="Why this is hard right now" description="Diagnosed from your own attempts, not guessed." />
          <Card className="diagnosis">
            <p className="diagnosis__advice">{diagnosis.advice}</p>

            {diagnosis.weakPrerequisites.length > 0 ? (
              <div className="diagnosis__group">
                <h3>Foundations to strengthen first</h3>
                <ul className="chip-list">
                  {diagnosis.weakPrerequisites.map((entry) => (
                    <li key={entry.conceptId}>
                      <Link to={`/concepts/${entry.conceptId}`} className="chip chip--missing">
                        {registry.concept(entry.conceptId)?.title ?? entry.conceptId} · {Math.round(entry.mastery * 100)}%
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {diagnosis.repeatedMisconceptions.length > 0 ? (
              <div className="diagnosis__group">
                <h3>Habits showing up in your answers</h3>
                <ul className="misconception-list">
                  {diagnosis.repeatedMisconceptions.map((entry) => {
                    const named = misconception(entry.id);
                    if (!named) return null;
                    return (
                      <li key={entry.id}>
                        <strong>{named.title}</strong> — seen {entry.count} times.
                        <p>{named.studentMessage}</p>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}
          </Card>
        </section>
      ) : null}

      {missing.length > 0 ? (
        <section className="dashboard-section">
          <SectionHeading title="Do these first" description="This concept is built on them." />
          <ul className="chip-list chip-list--large">
            {missing.map((id) => (
              <li key={id}>
                <Link to={`/concepts/${id}`} className="chip chip--missing">
                  {registry.concept(id)?.title ?? id}
                </Link>
              </li>
            ))}
          </ul>
          {route.length > 1 ? (
            <p className="page__note">
              The full route from where you are: {route.map((id) => registry.concept(id)?.title ?? id).join(' → ')}
            </p>
          ) : null}
        </section>
      ) : null}

      {lessons.length > 0 ? (
        <section className="dashboard-section">
          <SectionHeading title="Lessons" />
          <ul className="lesson-list">
            {lessons.map((lesson) => (
              <li key={lesson.id} className="lesson-row">
                <Link to={`/lessons/${lesson.id}`} className="lesson-row__main">
                  {/* The coin is filled and ticked when the lesson is done and
                      empty when it is not, so the two states differ in shape as
                      well as in colour. */}
                  <span
                    className={`lesson-row__status ${profile?.completedLessonIds.includes(lesson.id) ? 'is-done' : ''}`}
                    aria-hidden="true"
                  >
                    {profile?.completedLessonIds.includes(lesson.id) ? '✓' : ''}
                  </span>
                  <span>
                    <span className="lesson-row__title">{lesson.title}</span>
                    <span className="lesson-row__summary">{lesson.summary}</span>
                  </span>
                </Link>
                <span className="lesson-row__minutes">{lesson.estimatedMinutes} min</span>
              </li>
            ))}
          </ul>
          <div className="button-row">
            <Link to={`/practice/${conceptId}`} className="btn btn--primary">
              Practise this concept
            </Link>
            {/*
             * The warded site: the same practice with the world's clothes on.
             * Offered beside plain practice and never instead of it — the
             * encounter is behind a learner and the adventure flag, and this
             * page is public.
             */}
            {FEATURES.rpg ? (
              <Link to={`/adventure/encounter/${conceptId}`} className="btn btn--secondary">
                Face the warded site
              </Link>
            ) : null}
          </div>
        </section>
      ) : null}

      {unlocks.length > 0 ? (
        <section className="dashboard-section">
          <SectionHeading title="What this unlocks" description={`${unlocks.length} concepts depend on this one.`} />
          <ul className="chip-list">
            {unlocks.slice(0, 14).map((id) => (
              <li key={id}>
                <Link to={`/concepts/${id}`} className="chip">
                  {registry.concept(id)?.title ?? id}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
